import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type Dispatch,
  type ReactElement,
  type ReactNode,
} from 'react'
import type { ScanId, SavedPicture } from '../live/types.js'

export type ApplianceStatus = 'still-installed' | 'removed'

export type Material =
  | 'Laminate' | 'Quartz' | 'Granite' | 'Marble' | 'Butcher-block'
  | 'Concrete' | 'Tile' | 'Solid-surface' | 'Other'

export type ValveType = 'quarter-turn' | 'gate' | 'compression' | 'none-visible'
export type DrainType = 'standard-hose' | 'garbage-disposal' | 'none-visible'
export type OutletSide = 'left' | 'right' | 'back' | 'none-visible'

export interface ModelLabelFindings {
  brand: string
  model: string
  rawLabelText: string
}

export interface CountertopFindings {
  material: Material
  overhangInches: number
  materialConfidence: number
}

export interface UndersinkFindings {
  valve: ValveType
  drain: DrainType
  outlet: { present: boolean; side: OutletSide }
  drainLoopInches: number
}

export interface QAAnswers {
  level: 'level' | 'shimmed' | 'not-level' | 'not-sure'
  waterPressure: 'normal' | 'low' | 'not-sure'
  knownIssues: string  // freeform; 'No known issues' is the default chip
}

export interface BookingWindow {
  dateISO: string         // YYYY-MM-DD
  window: 'AM' | 'PM'
}

export type AddOnId = 'haulAway' | 'protectionPlan' | 'customMountingKit' | 'annualMaintenance'

/**
 * Per-scan in-flight estimates from `update_findings`. Reset when a scan
 * starts and overwritten in full by the corresponding `*_DETECTED` action
 * when the AI calls `advance_step`. The camera HUD chip rail reads from
 * here; results screens read from `modelLabel`/`countertop`/`undersink`.
 */
export interface LivePatches {
  'model-label'?: Partial<ModelLabelFindings>
  countertop?: Partial<CountertopFindings>
  undersink?: Partial<UndersinkFindings>
}

export interface DemoState {
  applianceStatus: ApplianceStatus | null
  modelLabel: ModelLabelFindings | null
  countertop: CountertopFindings | null
  undersink: UndersinkFindings | null
  qa: QAAnswers | null
  bookingWindow: BookingWindow | null
  addOns: AddOnId[]
  livePatches: LivePatches
  savedPictures: SavedPicture[]
}

export const initialState: DemoState = {
  applianceStatus: null,
  modelLabel: null,
  countertop: null,
  undersink: null,
  qa: null,
  bookingWindow: null,
  addOns: [],
  livePatches: {},
  savedPictures: [],
}

export type Action =
  | { type: 'SET_APPLIANCE_STATUS'; value: ApplianceStatus }
  | { type: 'MODEL_LABEL_DETECTED'; findings: ModelLabelFindings }
  | { type: 'COUNTERTOP_DETECTED'; findings: CountertopFindings }
  | { type: 'UNDERSINK_DETECTED'; findings: UndersinkFindings }
  | { type: 'OVERRIDE_MODEL_LABEL'; patch: Partial<ModelLabelFindings> }
  | { type: 'OVERRIDE_COUNTERTOP'; patch: Partial<CountertopFindings> }
  | { type: 'OVERRIDE_UNDERSINK'; patch: Partial<UndersinkFindings> }
  | { type: 'LIVE_FINDINGS_PATCH'; scan: ScanId; patch: Record<string, unknown> }
  | { type: 'CLEAR_LIVE_PATCH'; scan: ScanId }
  | { type: 'PICTURE_SAVED'; picture: SavedPicture }
  | { type: 'SET_QA'; value: QAAnswers }
  | { type: 'SET_BOOKING_WINDOW'; value: BookingWindow }
  | { type: 'TOGGLE_ADDON'; id: AddOnId }
  | { type: 'RESET' }

const mergeLivePatch = (
  state: DemoState,
  scan: ScanId,
  patch: Record<string, unknown>,
): DemoState => {
  const prior = state.livePatches[scan] ?? {}
  return {
    ...state,
    livePatches: { ...state.livePatches, [scan]: { ...prior, ...patch } },
  }
}

const clearLivePatch = (state: DemoState, scan: ScanId): DemoState => {
  if (!(scan in state.livePatches)) return state
  const next = { ...state.livePatches }
  delete next[scan]
  return { ...state, livePatches: next }
}

export const reducer = (state: DemoState, action: Action): DemoState => {
  switch (action.type) {
    case 'SET_APPLIANCE_STATUS':
      return { ...state, applianceStatus: action.value }
    case 'MODEL_LABEL_DETECTED':
      return clearLivePatch({ ...state, modelLabel: action.findings }, 'model-label')
    case 'COUNTERTOP_DETECTED':
      return clearLivePatch({ ...state, countertop: action.findings }, 'countertop')
    case 'UNDERSINK_DETECTED':
      return clearLivePatch({ ...state, undersink: action.findings }, 'undersink')
    case 'OVERRIDE_MODEL_LABEL':
      return state.modelLabel
        ? { ...state, modelLabel: { ...state.modelLabel, ...action.patch } }
        : state
    case 'OVERRIDE_COUNTERTOP':
      return state.countertop
        ? { ...state, countertop: { ...state.countertop, ...action.patch } }
        : state
    case 'OVERRIDE_UNDERSINK':
      return state.undersink
        ? { ...state, undersink: { ...state.undersink, ...action.patch } }
        : state
    case 'LIVE_FINDINGS_PATCH':
      return mergeLivePatch(state, action.scan, action.patch)
    case 'CLEAR_LIVE_PATCH':
      return clearLivePatch(state, action.scan)
    case 'PICTURE_SAVED':
      return { ...state, savedPictures: [...state.savedPictures, action.picture] }
    case 'SET_QA':
      return { ...state, qa: action.value }
    case 'SET_BOOKING_WINDOW':
      return { ...state, bookingWindow: action.value }
    case 'TOGGLE_ADDON':
      return {
        ...state,
        addOns: state.addOns.includes(action.id)
          ? state.addOns.filter(x => x !== action.id)
          : [...state.addOns, action.id],
      }
    case 'RESET':
      return initialState
  }
}

const STORAGE_KEY = 'veritact:demo-state:v1'

const loadFromStorage = (): DemoState => {
  if (typeof window === 'undefined') return initialState
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return initialState
    return { ...initialState, ...JSON.parse(raw) as DemoState }
  } catch {
    return initialState
  }
}

interface ContextValue {
  state: DemoState
  dispatch: Dispatch<Action>
}

const Ctx = createContext<ContextValue | null>(null)

export const DemoProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [state, dispatch] = useReducer(reducer, undefined, loadFromStorage)

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  return <Ctx.Provider value={{ state, dispatch }}>{children}</Ctx.Provider>
}

export const useDemoStore = (): ContextValue => {
  const v = useContext(Ctx)
  if (!v) throw new Error('useDemoStore called outside DemoProvider')
  return v
}
